import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchCurrentUserModule, completeUserModule, fetchUserModules } from '@/api/module';
import { toast } from '@/hooks/use-toast';
import { useMockWrapper } from '@/mocks/useMockWrapper';
import { createMockModule } from '@/mocks/mockConfig';
import { useNoGuard } from '@/hooks/useNoGuard';
import { useAuth } from '@/contexts/AuthContext';

export interface Module {
  id: number;
  name: string;
  introText: string;
  sequence: number;
  isActive: boolean;
  isRequired: boolean;
  isPaywalled: boolean;
  screenType: string;
  surveyId: number | null;
  data: string | null;
  
  // Phase 1: UI Content fields (data-driven attributes)
  uiTitle?: string;
  uiSubtitle?: string;
  uiDescription?: string;
  uiInstructions?: string;
  estimatedMinutes?: number;
  
  // Phase 1: Navigation fields
  completionRoute?: string;
  exitRoute?: string;
  
  // Phase 1: Button customization
  primaryButtonText?: string;
  secondaryButtonText?: string;
}

export interface UserModule {
  id: number;
  userId: number;
  moduleId: number;
  status: 'not_started' | 'in_progress' | 'completed';
  startedAt: string | null;
  progressCheckpoint: number;
  completedAt: string | null;
  userData: string | null;
  module: Module;
  isAdminOverride?: boolean;
  overrideInfo?: {
    createdAt: string;
    createdBy: string;
  };
}

/**
 * Real implementation of useCurrentUserModule without mock support
 */
function useRealCurrentUserModule() {
  const { authenticated } = useAuth();
  
  return useQuery<UserModule>({
    queryKey: ['current-user-module'],
    queryFn: fetchCurrentUserModule,
    retry: 1,
    enabled: authenticated // Only fetch when authenticated
  });
}

export function useCurrentUserModule() {
  return useMockWrapper(
    useRealCurrentUserModule,
    (pathname) => {
      const mockModule = createMockModule(pathname);
      return {
        data: mockModule ? {
          ...mockModule,
          startedAt: new Date().toISOString(),
          progressCheckpoint: 0,
          completedAt: null,
          userData: null,
          module: {
            ...mockModule.module,
            introText: `Welcome to ${mockModule.module.name}`,
            sequence: mockModule.module.order,
            isActive: true,
            isRequired: true,
            isPaywalled: false,
            surveyId: mockModule.module.surveyId ? parseInt(mockModule.module.surveyId) : null,
            // Add mock data-driven UI fields
            uiTitle: mockModule.module.name,
            uiDescription: `This is the ${mockModule.module.name} module`,
            primaryButtonText: "Get started",
            exitRoute: "/updates"
          }
        } as UserModule : undefined,
        isLoading: false,
        error: null,
        isError: false,
        isSuccess: true,
        status: 'success' as const,
        fetchStatus: 'idle' as const,
        isFetching: false,
        isRefetching: false,
        isPaused: false,
        isLoadingError: false,
        isRefetchError: false,
        dataUpdatedAt: Date.now(),
        errorUpdatedAt: 0,
        failureCount: 0,
        failureReason: null,
        errorUpdateCount: 0,
        isStale: false,
        isPlaceholderData: false,
        isPending: false,
        isFetched: true,
        isFetchedAfterMount: true,
        isInitialLoading: false,
        refetch: () => Promise.resolve({} as ReturnType<typeof useRealCurrentUserModule>),
        remove: () => {},
      };
    }
  );
}

/**
 * Hook to fetch all user modules (for progress tracking)
 */
export function useUserModules() {
  const noGuard = useNoGuard();
  const { authenticated } = useAuth();
  
  return useQuery<UserModule[]>({
    queryKey: ['user-modules'],
    queryFn: async () => {
      if (noGuard) {
        // In noguard mode, return mock module list
        console.log('[NoGuard Mode] Using mock user modules');
        return [
          {
            id: 1,
            userId: 1,
            moduleId: 1,
            status: 'completed',
            startedAt: new Date().toISOString(),
            progressCheckpoint: 0,
            completedAt: new Date().toISOString(),
            userData: null,
            module: {
              id: 1,
              name: 'Profile Setup',
              introText: 'Set up your profile',
              sequence: 1,
              isActive: true,
              isRequired: true,
              isPaywalled: false,
              screenType: 'profile',
              surveyId: null,
              data: null,
              uiTitle: 'Profile Setup',
              uiDescription: 'Complete your profile to get started',
              primaryButtonText: 'Get started',
              exitRoute: '/updates'
            }
          }
        ] as UserModule[];
      }
      return fetchUserModules();
    },
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: authenticated || noGuard // Only fetch when authenticated or in noguard mode
  });
}

export function useCompleteModule() {
  const queryClient = useQueryClient();
  const noGuard = useNoGuard();
  
  return useMutation({
    mutationFn: async (userModuleId: number) => {
      if (noGuard) {
        // In noguard mode, just log and return success
        console.log('[NoGuard Mode] Mock completing module:', userModuleId);
        return Promise.resolve({ success: true });
      }
      return completeUserModule(userModuleId);
    },
    onSuccess: () => {
      // Invalidate the current module query to fetch the next one
      queryClient.invalidateQueries({ queryKey: ['current-user-module'] });
      // Also invalidate user modules list
      queryClient.invalidateQueries({ queryKey: ['user-modules'] });
      toast({
        title: "Success",
        description: "Module completed successfully!",
      });
    },
    onError: (error) => {
      console.error('Failed to complete module:', error);
    }
  });
}